/**
 * SchoolDetailPage - Full management page for a single school
 * Has its own sidebar: Dashboard, Admissions, Students, Teachers, Classes, Subjects, Guardians, Settings, Academic Years
 */
import React, { useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { cn } from '@/lib/utils';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogClose } from '@/components/ui/dialog';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { ScrollArea } from '@/components/ui/scroll-area';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@/components/ui/tooltip';
import { toast } from 'sonner';
import {
  GraduationCap, Users, UserCog, BookOpen, ClipboardCheck,
  Plus, Search, Check, X, Loader2, School, Shield, Settings,
  ChevronLeft, ChevronRight, Home, ArrowLeft, Calendar, BookOpenCheck,
  LayoutDashboard, LogOut, Users2, Clock, FileText, Trophy, CalendarRange,
  BarChart3, Bell, Mail, Menu
} from 'lucide-react';
import { Bus, BookOpen as LibraryIcon, CreditCard, Globe, Eye, Banknote, MessageSquare, ArrowUpCircle, UserCheck, ShieldCheck, MessagesSquare, Briefcase, DollarSign, ClipboardList, Award, HeartHandshake, Zap } from 'lucide-react';
import { useAuthStore } from '@/lib/store';

import {
  SchoolDashboard, SchoolAdmissions, SchoolStudents,
  SchoolTeachers, SchoolClasses, SchoolSubjects, SchoolGuardians
} from '@/components/SchoolModule';

import { SchoolAttendance } from '@/components/school/SchoolAttendance';
import { SchoolTimetable } from '@/components/school/SchoolTimetable';
import { SchoolExams } from '@/components/school/SchoolExams';
import { SchoolReportCards } from '@/components/school/SchoolReportCards';
import { SchoolLMS } from '@/components/school/SchoolLMS';
import { SchoolAnalytics, SchoolAnnouncements, SchoolEmailSettings } from '@/components/school/SchoolAnalytics';
import { SchoolLibrary } from '@/components/school/SchoolLibrary';
import { SchoolTransport } from '@/components/school/SchoolTransport';
import { SchoolHousing } from '@/components/school/SchoolHousing';
import { SchoolDining } from '@/components/school/SchoolDining';
import { SchoolPOS } from '@/components/school/SchoolPOS';
import { SchoolAccess } from '@/components/school/SchoolAccess';
import { SchoolCMS } from '@/components/school/SchoolCMS';
import { SchoolViewAs } from '@/components/school/SchoolViewAs';
import { SchoolFees } from '@/components/school/SchoolFees';
import { SchoolNotifications } from '@/components/school/SchoolNotifications';
import { SchoolPromotions, SchoolGraduation, SchoolAlumni, StudentTimeline } from '@/components/school/SchoolLifecycle';
import { SchoolAlumniHub } from '@/components/school/SchoolAlumniHub';
import { SchoolTeam } from '@/components/school/SchoolTeam';
import { SchoolMessages } from '@/components/school/SchoolMessages';
import { SchoolCommunicationEngine } from '@/components/school/SchoolCommunicationEngine';
import { SchoolReportsAnalytics } from '@/components/school/SchoolReportsAnalytics';
import { SchoolGamification } from '@/components/school/SchoolGamification';
import { SchoolScheduler } from '@/components/school/SchoolScheduler';
import { AdminAlertBell } from '@/components/school/AdminAlertBell';
import ReportIssueButton from '@/components/ReportIssueButton';
import { TenantBroadcastBanner } from '@/components/superadmin/TenantBroadcastBanner';
import { SchoolSystemSettings } from '@/components/school/SchoolSystemSettings';
import { SchoolCertificates } from '@/components/school/SchoolCertificates';
import { SchoolHR } from '@/components/school/SchoolHR';
import SchoolBehaviour from '@/components/school/SchoolBehaviour';
import SchoolSafeguarding from '@/components/school/SchoolSafeguarding';
import SchoolSupport from '@/components/school/SchoolSupport';
import { SchoolFrontOffice } from '@/components/school/SchoolFrontOffice';
import ScopedBiblePlans from '@/components/bible/ScopedBiblePlans';
import ScopedGatePass from '@/components/school/ScopedGatePass';
import ScopedIncidents from '@/components/school/ScopedIncidents';
import { CampusContextBar } from '@/components/school/CampusContext';
import { AcademicContextBar } from '@/components/school/AcademicContext';
import SemesterReport from '@/components/school/SemesterReport';
import AdmissionsWorkspace from '@/components/school/AdmissionsWorkspace';
import SchoolAcademic from '@/components/school/SchoolAcademic';
import SchoolFaculties from '@/components/school/SchoolFaculties';
import SchoolTransfers from '@/components/school/SchoolTransfers';

const API_URL = process.env.REACT_APP_BACKEND_URL;
const api = async (path, opts = {}) => {
  const res = await fetch(`${API_URL}/api/school${path}`, {
    ...opts,
    headers: { 'Authorization': `Bearer ${getToken()}`, 'Content-Type': 'application/json', ...opts.headers },
  });
  if (!res.ok) {
    const err = await res.json().catch(() => ({}));
    throw new Error(err.detail || 'Request failed');
  }
  return res.json();
};

// Sidebar menu — extracted to schoolMenuConfig.js for maintainability
import { SCHOOL_MENU_GROUPS, SCHOOL_MENU } from '@/components/school/schoolMenuConfig';
import SchoolCampusesPage from '@/pages/SchoolCampusesPage';


// ==================== ACADEMIC YEARS TAB ====================
// AcademicYearsPanel extracted to components/school/AcademicYearsPanel.jsx
import AcademicYearsPanel from '@/components/school/AcademicYearsPanel';



// ==================== SCHOOL SETTINGS TAB ====================
// SchoolSettingsPanel extracted to components/school/SchoolSettingsPanel.jsx
import SchoolSettingsPanel from '@/components/school/SchoolSettingsPanel';
import { getToken } from '@/lib/authToken';



// ==================== MAIN SCHOOL DETAIL PAGE ====================
const SchoolDetailPage = () => {
  const { tenantId } = useParams();
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState('dashboard');
  const [collapsed, setCollapsed] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);
  const [menuSearch, setMenuSearch] = useState('');
  const { logout, user } = useAuthStore();

  // Scope-isolation guard: a tenant-scoped admin (school admin) must not view another school's dashboard.
  // Platform super-admins can view any tenant.
  const isSuperAdmin = user && (user.is_super_admin === true || user.role === 'super_admin'
    || (Array.isArray(user.permissions) && (user.permissions.includes('*') || user.permissions.includes('super_admin'))));
  React.useEffect(() => {
    if (user && !isSuperAdmin && user.tenant_id && user.tenant_id !== tenantId) {
      navigate(`/admin/schools/${user.tenant_id}`, { replace: true });
    }
  }, [user, isSuperAdmin, tenantId, navigate]);

  const { data: tenant, isLoading } = useQuery({
    queryKey: ['school-tenant', tenantId],
    queryFn: () => api(`/tenants/${tenantId}`),
    enabled: !!tenantId,
  });

  if (isLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-slate-50">
        <Loader2 className="h-8 w-8 animate-spin text-indigo-500" />
      </div>
    );
  }

  if (!tenant) {
    return (
      <div className="min-h-screen flex flex-col items-center justify-center bg-slate-50 gap-4">
        <p className="text-slate-500">School not found</p>
        <Button onClick={() => navigate('/admin/schools')}>Back to Schools</Button>
      </div>
    );
  }

  const renderContent = () => {
    switch (activeTab) {
      case 'dashboard': return <SchoolDashboard tenantId={tenantId} onNavigate={setActiveTab} />;
      case 'campuses': return <SchoolCampusesPage tenantId={tenantId} />;
      case 'faculties': return <SchoolFaculties tenantId={tenantId} />;
      case 'academic': return <SchoolAcademic tenantId={tenantId} />;
      case 'admissions': return <SchoolAdmissions tenantId={tenantId} />;
      case 'admissions-pipeline': return <AdmissionsWorkspace tenantId={tenantId} />;
      case 'students': return <SchoolStudents tenantId={tenantId} />;
      case 'transfers': return <SchoolTransfers tenantId={tenantId} />;
      case 'teachers': return <SchoolTeachers tenantId={tenantId} />;
      case 'hr': return <SchoolHR tenantId={tenantId} />;
      case 'front_office': return <SchoolFrontOffice tenantId={tenantId} />;
      case 'classes': return <SchoolClasses tenantId={tenantId} />;
      case 'subjects': return <SchoolSubjects tenantId={tenantId} />;
      case 'guardians': return <SchoolGuardians tenantId={tenantId} />;
      case 'attendance': return <SchoolAttendance tenantId={tenantId} />;
      case 'timetable': return <SchoolTimetable tenantId={tenantId} />;
      case 'exams': return <SchoolExams tenantId={tenantId} />;
      case 'report-cards': return <SchoolReportCards tenantId={tenantId} />;
      case 'lms': return <SchoolLMS tenantId={tenantId} />;
      case 'analytics': return <SchoolAnalytics tenantId={tenantId} />;
      case 'announcements': return <SchoolAnnouncements tenantId={tenantId} />;
      case 'email-settings': return <SchoolEmailSettings tenantId={tenantId} />;
      case 'library': return <SchoolLibrary tenantId={tenantId} />;
      case 'transport': return <SchoolTransport tenantId={tenantId} />;
      case 'housing': return <SchoolHousing tenantId={tenantId} />;
      case 'dining': return <SchoolDining tenantId={tenantId} />;
      case 'pos': return <SchoolPOS tenantId={tenantId} />;
      case 'access': return <SchoolAccess tenantId={tenantId} />;
      case 'fees': return <SchoolFees tenantId={tenantId} />;
      case 'team': return <SchoolTeam tenantId={tenantId} />;
      case 'pt-messages': return <SchoolMessages tenantId={tenantId} />;
      case 'comm-engine': return <SchoolCommunicationEngine tenantId={tenantId} />;
      case 'reports-f': return <SchoolReportsAnalytics tenantId={tenantId} />;
      case 'gamification-g': return <SchoolGamification tenantId={tenantId} />;
      case 'scheduler': return <SchoolScheduler tenantId={tenantId} />;
      case 'promotions': return <SchoolPromotions tenantId={tenantId} />;
      case 'graduation': return <SchoolGraduation tenantId={tenantId} />;
      case 'alumni': return <SchoolAlumni tenantId={tenantId} />;
      case 'alumni-hub': return <SchoolAlumniHub tenantId={tenantId} />;
      case 'notifications': return <SchoolNotifications tenantId={tenantId} />;
      case 'website': return <SchoolCMS tenantId={tenantId} />;
      case 'view-as': return <SchoolViewAs tenantId={tenantId} />;
      case 'behaviour': return <SchoolBehaviour tenantId={tenantId} />;
      case 'safeguarding': return <SchoolSafeguarding tenantId={tenantId} />;
      case 'bible-plans': return <ScopedBiblePlans scopeType="school" scopeId={tenantId} isAdmin tenantName={tenant?.name} />;
      case 'gate-pass': return <ScopedGatePass tenantId={tenantId} />;
      case 'incidents': return <ScopedIncidents tenantId={tenantId} />;
      case 'support-inbox': return <SchoolSupport tenantId={tenantId} />;
      case 'academic-years': return <AcademicYearsPanel tenantId={tenantId} />;
      case 'semester-report': return <SemesterReport tenantId={tenantId} />;
      case 'settings': return <SchoolSystemSettings tenantId={tenantId} />;
      case 'certificates': return <SchoolCertificates tenantId={tenantId} />;
      default: return <SchoolDashboard tenantId={tenantId} onNavigate={setActiveTab} />;
    }
  };

  const brandColor = tenant.primary_color || '#6366f1';

  const handleMenuClick = (id) => {
    setActiveTab(id);
    setMobileOpen(false);
  };

  return (
    <TooltipProvider delayDuration={0}>
      <div className="flex h-screen bg-slate-50" data-testid="school-detail-page">
        {/* Mobile overlay backdrop */}
        {mobileOpen && (
          <div className="fixed inset-0 bg-black/50 z-40 md:hidden" onClick={() => setMobileOpen(false)} data-testid="mobile-overlay" />
        )}

        {/* Sidebar */}
        <div className={cn(
          "h-full bg-slate-900 text-white transition-all duration-300 shrink-0 z-50",
          // Desktop: normal flow, hidden on mobile
          "hidden md:flex md:flex-col",
          collapsed ? "md:w-[68px]" : "md:w-[250px]",
          // Mobile: overlay when open
          mobileOpen && "!flex !flex-col fixed inset-y-0 left-0 w-full max-w-[260px]"
        )}>
          {/* Header */}
          <div className="p-4 border-b border-slate-800">
            <div className="flex items-center justify-between">
              {!collapsed && (
                <div className="flex items-center gap-2 min-w-0">
                  {tenant.logo_url ? (
                    <img
                      src={tenant.logo_url.startsWith('http') ? tenant.logo_url : `${process.env.REACT_APP_BACKEND_URL}${tenant.logo_url}`}
                      alt={`${tenant.name} logo`}
                      className="w-8 h-8 rounded-lg object-cover shrink-0 bg-white"
                      data-testid="school-sidebar-logo"
                    />
                  ) : (
                    <div
                      className="w-8 h-8 rounded-lg flex items-center justify-center shrink-0"
                      style={{ backgroundColor: brandColor }}
                    >
                      <GraduationCap className="h-4 w-4 text-white" />
                    </div>
                  )}
                  <div className="min-w-0">
                    <h1 className="font-bold text-sm truncate">{tenant.name}</h1>
                    <p className="text-xs text-slate-400">{tenant.code}</p>
                  </div>
                </div>
              )}
              {collapsed && (
                tenant.logo_url ? (
                  <img
                    src={tenant.logo_url.startsWith('http') ? tenant.logo_url : `${process.env.REACT_APP_BACKEND_URL}${tenant.logo_url}`}
                    alt={`${tenant.name} logo`}
                    className="w-8 h-8 rounded-lg object-cover mx-auto bg-white"
                  />
                ) : (
                  <div
                    className="w-8 h-8 rounded-lg flex items-center justify-center mx-auto"
                    style={{ backgroundColor: brandColor }}
                  >
                    <GraduationCap className="h-4 w-4 text-white" />
                  </div>
                )
              )}
              {/* Desktop collapse button */}
              <Button
                variant="ghost"
                size="icon"
                onClick={() => setCollapsed(!collapsed)}
                className="text-slate-400 hover:text-white hover:bg-slate-800 shrink-0 hidden md:flex"
              >
                {collapsed ? <ChevronRight className="h-4 w-4" /> : <ChevronLeft className="h-4 w-4" />}
              </Button>
              {/* Mobile close button */}
              <Button
                variant="ghost"
                size="icon"
                onClick={() => setMobileOpen(false)}
                className="text-slate-400 hover:text-white hover:bg-slate-800 shrink-0 md:hidden"
              >
                <X className="h-4 w-4" />
              </Button>
            </div>

            {/* Back to Schools */}
            <div className={cn("mt-3", collapsed && "md:flex md:justify-center")}>
              <Tooltip>
                <TooltipTrigger asChild>
                  <Button
                    variant="outline"
                    size={collapsed ? "icon" : "sm"}
                    onClick={() => navigate('/admin/schools')}
                    className={cn(
                      "bg-slate-800 border-slate-700 text-slate-300 hover:bg-slate-700 hover:text-white",
                      !collapsed && "w-full justify-start gap-2"
                    )}
                    data-testid="back-to-schools-btn"
                  >
                    <ArrowLeft className="h-4 w-4" />
                    {!collapsed && <span>All Schools</span>}
                  </Button>
                </TooltipTrigger>
                {collapsed && <TooltipContent side="right">All Schools</TooltipContent>}
              </Tooltip>
            </div>
          </div>

          {/* Menu — grouped by functional area */}
          <ScrollArea className="flex-1 py-2">
            <div className="px-2 space-y-4">
              {/* Sidebar quick search — filters menu items by label */}
              {!collapsed && (
                <div className="px-1 sticky top-0 z-10 pb-1 bg-slate-900">
                  <div className="relative">
                    <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-500" />
                    <Input
                      type="text"
                      value={menuSearch}
                      onChange={(e) => setMenuSearch(e.target.value)}
                      placeholder="Search menu…"
                      data-testid="school-sidebar-search"
                      className="pl-9 pr-8 h-9 bg-slate-800 border-slate-700 text-white placeholder:text-slate-500 focus-visible:ring-indigo-500"
                    />
                    {menuSearch && (
                      <button
                        type="button"
                        onClick={() => setMenuSearch('')}
                        className="absolute right-2 top-1/2 -translate-y-1/2 text-slate-500 hover:text-white"
                        data-testid="school-sidebar-search-clear"
                        aria-label="Clear search"
                      >
                        <X className="h-3.5 w-3.5" />
                      </button>
                    )}
                  </div>
                </div>
              )}
              {(() => {
                const q = menuSearch.trim().toLowerCase();
                // Faculties and Deans exist for colleges and universities only.
                const isTertiary = ['college', 'university'].includes(tenant?.type || '');
                const groups = SCHOOL_MENU_GROUPS
                  .map(g => ({ ...g, items: g.items.filter(i => !i.tertiaryOnly || isTertiary) }))
                  .filter(g => g.items.length > 0);
                const filtered = q
                  ? groups
                      .map(g => ({ ...g, items: g.items.filter(i => i.label.toLowerCase().includes(q)) }))
                      .filter(g => g.items.length > 0)
                  : groups;
                if (q && filtered.length === 0) {
                  return (
                    <p className="px-3 py-6 text-center text-xs text-slate-500" data-testid="school-sidebar-no-match">
                      No menu items match "{menuSearch}"
                    </p>
                  );
                }
                return filtered.map(group => (
                <div key={group.id} data-testid={`nav-group-${group.id}`}>
                  {!collapsed && (
                    <p className="px-3 pb-1 text-[10px] font-semibold uppercase tracking-wider text-slate-500">
                      {group.label}
                    </p>
                  )}
                  <div className="space-y-0.5">
                    {group.items.map(item => {
                      const isActive = activeTab === item.id;
                      return (
                        <Tooltip key={item.id}>
                          <TooltipTrigger asChild>
                            <button
                              onClick={() => handleMenuClick(item.id)}
                              className={cn(
                                "w-full flex items-center gap-3 rounded-lg text-sm transition-colors",
                                collapsed ? "justify-center p-2.5" : "px-3 py-2",
                                isActive
                                  ? "bg-indigo-600 text-white"
                                  : "text-slate-400 hover:text-white hover:bg-slate-800"
                              )}
                              data-testid={`school-nav-${item.id}`}
                            >
                              <item.icon className="h-4 w-4 shrink-0" />
                              {!collapsed && <span className="truncate">{item.label}</span>}
                            </button>
                          </TooltipTrigger>
                          {collapsed && <TooltipContent side="right">{item.label}</TooltipContent>}
                        </Tooltip>
                      );
                    })}
                  </div>
                </div>
                ));
              })()}
            </div>
          </ScrollArea>

          {/* Footer */}
          <div className="p-3 border-t border-slate-800">
            <Tooltip>
              <TooltipTrigger asChild>
                <Button
                  variant="ghost"
                  size={collapsed ? "icon" : "sm"}
                  onClick={() => navigate('/admin')}
                  className={cn(
                    "text-slate-400 hover:text-white hover:bg-slate-800",
                    !collapsed && "w-full justify-start gap-2"
                  )}
                  data-testid="back-to-admin-btn"
                >
                  <Home className="h-4 w-4" />
                  {!collapsed && <span>Admin Home</span>}
                </Button>
              </TooltipTrigger>
              {collapsed && <TooltipContent side="right">Admin Home</TooltipContent>}
            </Tooltip>
          </div>
        </div>

        {/* Main Content */}
        <div className="flex-1 overflow-auto min-w-0">
          {/* Mobile header with hamburger */}
          <div className="md:hidden flex items-center gap-3 px-4 py-3 bg-white border-b sticky top-0 z-30" data-testid="mobile-header">
            <Button variant="ghost" size="icon" onClick={() => setMobileOpen(true)} data-testid="mobile-menu-btn" className="shrink-0">
              <Menu className="h-5 w-5" />
            </Button>
            <div className="flex items-center gap-2 min-w-0 flex-1">
              <div className="w-6 h-6 rounded flex items-center justify-center shrink-0" style={{ backgroundColor: brandColor }}>
                <GraduationCap className="h-3 w-3 text-white" />
              </div>
              <span className="font-semibold text-sm truncate">{tenant.name}</span>
            </div>
            <AdminAlertBell tenantId={tenantId} />
          </div>
          <div className="hidden md:flex items-center justify-end gap-2 px-6 py-2 bg-white border-b sticky top-0 z-20" data-testid="desktop-header">
            <ReportIssueButton scopeType="school" scopeId={tenantId} scopeLabel={tenant?.name} />
            <AdminAlertBell tenantId={tenantId} />
          </div>
          <div className="p-3 sm:p-4 md:p-6 max-w-7xl mx-auto">
            <TenantBroadcastBanner tenantId={tenantId} />
            <CampusContextBar tenantId={tenantId} />
            <AcademicContextBar tenantId={tenantId} />
            {renderContent()}
          </div>
        </div>
      </div>
    </TooltipProvider>
  );
};

export default SchoolDetailPage;

